import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getSafeCssSelector, highlightRow } from './utils';
import styles from '../Table.module.scss';
import useTableStore from '../store/useTableStore';

const HIGHLIGHT_RETRY_MAX_ATTEMPTS = 10;
const HIGHLIGHT_RETRY_DELAY_MS = 150;
const HIGHLIGHT_INITIAL_DELAY_SAME_PAGE_MS = 100;
const HIGHLIGHT_INITIAL_DELAY_DIFFERENT_PAGE_MS = 500;

export interface RowData {
  [key: string]: unknown;
}

const isRowDataRecord = (value: unknown): value is RowData => typeof value === 'object' && value !== null;

const getRowFieldValue = (row: unknown, field: string): unknown => {
  if (!isRowDataRecord(row)) {
    return undefined;
  }

  return row[field];
};

interface UseRowHighlightParams<T> {
  id: string;
  fullData: T[];
  currentData: T[];
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  setPagination: (id: string, pagination: Partial<{ pageIndex: number; pageSize: number }>) => void;
}

/**
 * Hook to manage row highlighting in paginated tables.
 * Extracts highlight parameters from URL query string, locates the target row in full dataset,
 * navigates to the correct page if needed, and applies a flash animation to highlight the row.
 * Implements retry logic to handle async DOM rendering and prevents duplicate highlighting.
 * @template T - Row data type
 * @param params - Configuration object containing table data, pagination state, and callbacks
 * @returns Object containing the set of processed highlight IDs
 */
export function useRowHighlight<T>({
  id,
  fullData,
  currentData,
  pagination,
  setPagination,
}: UseRowHighlightParams<T>) {
  const processedHighlightIdsRef = useRef<Set<string>>(new Set());
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const highlightField = params.get('highlightField');
  const highlightValue = params.get('highlightValue');

  const { setHighlightedFieldAndValue } = useTableStore(x => ({
    setHighlightedFieldAndValue: x.setHighlightedFieldAndValue,
  }));

  useEffect(() => {
    if (pagination.pageSize === 0) {
      setPagination(id, { pageIndex: 0 });
      return;
    }

    if (!highlightField || !highlightValue) {
      return;
    }

    const highlightKey = `${highlightField}-${highlightValue}`;
    if (processedHighlightIdsRef.current.has(highlightKey)) {
      return;
    }

    const globalRowIndex = fullData.findIndex(dataRow => {
      const rowValue = String(getRowFieldValue(dataRow, highlightField));
      const paramValue = String(highlightValue);
      return rowValue === paramValue;
    });

    if (globalRowIndex === -1) return;

    const targetPageIndex = Math.floor(globalRowIndex / pagination.pageSize);
    const isAlreadyOnCorrectPage = targetPageIndex === pagination.pageIndex;

    if (!isAlreadyOnCorrectPage) {
      setPagination(id, { pageIndex: targetPageIndex });
    }

    setHighlightedFieldAndValue(id, highlightField, highlightValue);

    let retryCount = 0;
    const selector = getSafeCssSelector(highlightField, highlightValue);

    const tryHighlight = () => {
      const rowElement = document.querySelector(selector);

      if (rowElement) {
        processedHighlightIdsRef.current.add(highlightKey);
        highlightRow(highlightField, highlightValue, styles.flash);
      } else if (retryCount < HIGHLIGHT_RETRY_MAX_ATTEMPTS) {
        retryCount++;
        setTimeout(tryHighlight, HIGHLIGHT_RETRY_DELAY_MS);
      }
    };

    const initialDelay = isAlreadyOnCorrectPage
      ? HIGHLIGHT_INITIAL_DELAY_SAME_PAGE_MS
      : HIGHLIGHT_INITIAL_DELAY_DIFFERENT_PAGE_MS;
    const timeoutId = setTimeout(tryHighlight, initialDelay);
    return () => clearTimeout(timeoutId);
  }, [
    id,
    fullData,
    currentData,
    pagination.pageSize,
    pagination.pageIndex,
    setPagination,
    highlightField,
    highlightValue,
    setHighlightedFieldAndValue,
  ]);

  return { processedHighlightIds: processedHighlightIdsRef.current };
}
